import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams, useRouteMatch } from '@fs/zion-router'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import { FamilyDetailsView } from './familyView/FamilyDetailsView'
import { buttonTemplate, navigationQueryParams, queueNames } from '../messagesList/useMessagesListController'
import { deleteLastPathElement, useQueryParams } from '../../../util/UrlUtil'
import SimpleMessageDetailsView from './SimpleMessageDetailsView'
import { HolocaustReportDetailsView } from './HolocaustReportDetailsView'
import { useQueueButtons } from '../messagesList/hooks/useQueueButtons'

export default function MessageDetails() {
  usePageView({ page_detail: 'DMConsole:MessageDetails' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.queues.messageDetails'))
  // Navigation stuff
  const pathParams = useParams()
  const queryParams = useQueryParams()
  const match = useRouteMatch()
  const history = useHistory()
  const backToMessagesList = useCallback(() => {
    history.push(
      `${deleteLastPathElement(match.url)}?${navigationQueryParams({
        queuePageIndex: queryParams.get('queuePageIndex'),
        personId: queryParams.get('personId'),
        treeCategories: queryParams.get('treeCategories'),
      })}`
    )
  }, [history, match.url, queryParams])
  const { queueButtons, queueButtonsDialogs } = useQueueButtons()

  const viewMap = [
    {
      match: (queueName) => queueName.includes(queueNames.HOLOCAUST_ORDINANCE_REPORT_QUEUES),
      View: HolocaustReportDetailsView,
      buttonTemplates: [buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessageById)],
    },
    {
      match: (queueName) =>
        [
          queueNames.RESERVATION_HELD_BY_NON_CONTRIBUTORS,
          queueNames.ONE_HUNDRED_TEN_YEARS_QUEUES,
          queueNames.PROFANITY_QUEUES,
          queueNames.DEITY_QUEUES,
          queueNames.BIRTH_DATE_CHANGE_WITH_RESERVATIONS_QUEUES,
          queueNames.SEX_CHANGED_QUEUES,
          queueNames.SLAVERY_ASSOCIATION,
          queueNames.MEMORIES_ADDED,
        ].some((q) => queueName.includes(q)),
      View: SimpleMessageDetailsView,
      buttonTemplates: [buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessageById)],
    },
    {
      match: (queueName) => queueName.includes(queueNames.HOLOCAUST_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('queues.holocaust', 'Holocaust'), null, queueButtonsDialogs.holocaustDialog),
        buttonTemplate(
          t('queues.holocaust.notHolocaust', 'Not Holocaust'),
          null,
          queueButtonsDialogs.notHolocaustDialog
        ),
        buttonTemplate(t('queues.holocaust.yadVashem', 'Yad Vashem'), queueButtons.queueButtonYadVashem),
      ],
      props: {
        personLinkInNewTab: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.FAMOUS_PERSON_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('famous', 'Famous'), null, queueButtonsDialogs.famousDialog),
        buttonTemplate(t('queues.notFamous', 'Not Famous'), queueButtons.queueButtonNotFamous),
      ],
      props: {
        goBackAfterButtonCallback: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.RELIGIOUS_TITLE_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('action.restrict', 'Restrict'), queueButtons.queueButtonReligiousTitle),
        buttonTemplate(t('queues.unrestrict', 'Unrestrict'), queueButtons.queueButtonNotReligiousTitle),
        buttonTemplate(t('queues.google', 'Google'), queueButtons.queueButtonGoogle),
      ],
      props: {
        goBackAfterButtonCallback: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.HISTORICAL_PERSON_QUEUES),
      View: SimpleMessageDetailsView,
      buttonTemplates: [
        buttonTemplate(t('action.restrict', 'Restrict'), queueButtons.queueButtonHistorical),
        buttonTemplate(t('queues.unrestrict', 'Unrestrict'), queueButtons.queueButtonNotHistorical),
        buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessageById),
      ],
      props: {
        goBackAfterButtonCallback: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.HYBRID_SENSITIVE_TRANSITION_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('queues.transition', 'Transition'), queueButtons.queueButtonTransition),
        buttonTemplate(t('queues.notTransition', 'Not Transition'), queueButtons.queueButtonNotTransition),
      ],
      props: {
        goBackAfterButtonCallback: true,
        showViewButton: false,
        showButtonsJustPrimary: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.HYBRID_SENSITIVE_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('queues.hybridPublic', 'Hybrid Public'), queueButtons.queueButtonHybridPublic),
        buttonTemplate(t('queues.hybridPrivate', 'Hybrid Private'), queueButtons.queueButtonHybridPrivate),
        buttonTemplate(t('queues.notHybrid', 'Not Hybrid'), queueButtons.queueButtonNotHybrid),
        buttonTemplate(t('queues.forebears', 'Forebears'), queueButtons.queueButtonForebears),
        buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive),
      ],
      props: {
        goBackAfterButtonCallback: true,
        showViewButton: false,
        showButtonsJustPrimary: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.SENSITIVE_REEVALUATION_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive),
        buttonTemplate(t('queues.notSensitive', 'Not Sensitive'), queueButtons.queueButtonNotSensitive),
        buttonTemplate(t('queues.hybridPrivate', 'Hybrid Private'), queueButtons.queueButtonHybridPrivate),
        buttonTemplate(t('queues.forebears', 'Forebears'), queueButtons.queueButtonForebears),
      ],
      props: {
        goBackAfterButtonCallback: true,
        showViewButton: false,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.SENSITIVE_QUEUES),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive),
        buttonTemplate(t('queues.notSensitive', 'Not Sensitive'), queueButtons.queueButtonNotSensitive),
        buttonTemplate(t('queues.forebears', 'Forebears'), queueButtons.queueButtonForebears),
      ],
      props: {
        goBackAfterButtonCallback: true,
        showViewButton: false,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.MYTHOLOGICAL),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('mythological', 'Mythological'), queueButtons.queueButtonMythological),
        buttonTemplate(t('queues.notMythological', 'Not Mythological'), queueButtons.queueButtonNotMythological),
        buttonTemplate(t('queues.google', 'Google'), queueButtons.queueButtonGoogle),
      ],
      props: {
        goBackAfterButtonCallback: true,
      },
    },
    {
      match: (queueName) => queueName.includes(queueNames.FICTITIOUS),
      View: FamilyDetailsView,
      buttonTemplates: [
        buttonTemplate(t('fictitious', 'Fictitious'), queueButtons.queueButtonFictitious),
        buttonTemplate(t('queues.notFictitious', 'Not Fictitious'), queueButtons.queueButtonNotFictitious),
      ],
      props: {
        goBackAfterButtonCallback: true,
      },
    },
  ]

  // Find the first matching view config
  const found = viewMap.find((entry) => entry.match(pathParams.queueName))
  if (found) {
    const { View, buttonTemplates, props = {} } = found
    return <View buttonTemplates={buttonTemplates} backToMessagesList={backToMessagesList} {...props} />
  }
  // Default fallback view
  return (
    <SimpleMessageDetailsView
      buttonTemplates={[buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessageById)]}
    />
  )
}
